import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { dashboardAPI, tauxAPI, userAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { 
  FileText, 
  Clock, 
  AlertTriangle,
  Wallet,
  Calculator,
  CheckCircle,
  Receipt,
  ExternalLink,
  Calendar,
  ArrowDown,
  ArrowUp,
  Euro,
  TrendingUp,
  Info,
  Shield
} from 'lucide-react';

const DashboardPage = () => {
  const { user, setUser } = useAuth();
  const [stats, setStats] = useState(null);
  const [taux, setTaux] = useState(null);
  const [loading, setLoading] = useState(true);
  
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  
  const [typeActivite, setTypeActivite] = useState(() => {
    return user?.type_activite || localStorage.getItem('ae_type_activite') || 'BIC_SERVICE';
  });
  
  const [isTvaSubject, setIsTvaSubject] = useState(() => {
    return user?.is_tva_subject !== undefined ? user.is_tva_subject : true;
  });
  
  const [versementLiberatoire, setVersementLiberatoire] = useState(() => {
    return user?.versement_liberatoire !== undefined ? user.versement_liberatoire : false;
  });
  
  const [checklist, setChecklist] = useState(() => {
    const saved = localStorage.getItem('ae_checklist');
    if (saved) return JSON.parse(saved);
    return [
      { id: 1, label: 'Déclarations URSSAF', done: false },
      { id: 2, label: 'Factures du mois envoyées', done: false },
      { id: 3, label: 'Relancer les impayés', done: false },
      { id: 4, label: 'Archivage documents', done: false },
    ];
  });

  const loadStats = useCallback(async () => {
    try {
      setLoading(true);
      const response = await dashboardAPI.getStats(selectedMonth);
      setStats(response.data);
    } catch (error) {
      // Silently handle error in production
    } finally {
      setLoading(false);
    }
  }, [selectedMonth]);
  
  const loadTaux = useCallback(async () => {
    try {
      const response = await tauxAPI.get();
      setTaux(response.data);
    } catch (error) {
      // Silently handle error in production
    }
  }, []);

  const saveUserSettings = useCallback(async (settings) => {
    try {
      const response = await userAPI.updateSettings(settings);
      if (setUser) {
        setUser(response.data);
      }
    } catch (error) {
      // Silently handle error in production
    }
  }, [setUser]);

  useEffect(() => {
    loadStats();
    loadTaux();
  }, [loadStats, loadTaux]);

  useEffect(() => {
    localStorage.setItem('ae_checklist', JSON.stringify(checklist));
  }, [checklist]);
  
  useEffect(() => {
    localStorage.setItem('ae_type_activite', typeActivite);
    saveUserSettings({ type_activite: typeActivite });
  }, [typeActivite, saveUserSettings]);
  
  useEffect(() => {
    saveUserSettings({ is_tva_subject: isTvaSubject });
  }, [isTvaSubject, saveUserSettings]);
  
  useEffect(() => {
    saveUserSettings({ versement_liberatoire: versementLiberatoire });
  }, [versementLiberatoire, saveUserSettings]);

  const toggleChecklistItem = (id) => {
    setChecklist(prev => prev.map(item => 
      item.id === id ? { ...item, done: !item.done } : item
    ));
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(amount || 0);
  };
  
  const getTauxURSSAF = () => {
    if (!taux) return 21.2;
    switch(typeActivite) {
      case 'BIC_VENTE': return taux.taux_urssaf_bic_vente || 12.3;
      case 'BIC_SERVICE': return taux.taux_urssaf_bic_service || 21.2;
      case 'BNC': return taux.taux_urssaf_bnc || 25.6;
      case 'BNC_CIPAV': return taux.taux_urssaf_bnc_cipav || 23.2;
      default: return 21.2;
    }
  };
  
  // Taux du versement libératoire de l'impôt sur le revenu
  const getTauxVersementLiberatoire = () => {
    switch(typeActivite) {
      case 'BIC_VENTE': return 1.0;   // 1% pour vente
      case 'BIC_SERVICE': return 1.7; // 1.7% pour services
      case 'BNC': return 2.2;         // 2.2% pour BNC
      case 'BNC_CIPAV': return 2.2;
      default: return 1.7;
    }
  };
  
  // Taux CFP (Contribution Formation Professionnelle)
  const getTauxCFP = () => {
    switch(typeActivite) {
      case 'BIC_VENTE': return 0.1;
      case 'BIC_SERVICE': return 0.3;
      case 'BNC': return 0.2;
      case 'BNC_CIPAV': return 0.2;
      default: return 0.2;
    }
  };
  
  // Données
  const mois = stats?.mois || {};
  const annee = stats?.annee || {};
  
  // Calculs MOIS
  const tauxURSSAF = getTauxURSSAF();
  const tauxVL = versementLiberatoire ? getTauxVersementLiberatoire() : 0;
  const tauxCFP = getTauxCFP();
  const tauxTotal = tauxURSSAF + tauxVL + tauxCFP;
  
  // URSSAF: sur HT si assujetti TVA, sur TTC sinon (franchise de TVA)
  const baseUrssafMois = isTvaSubject ? (mois.ca_encaisse_ht || 0) : (mois.ca_encaisse || 0);
  const urssafMois = baseUrssafMois * (tauxURSSAF / 100);
  const vlMois = baseUrssafMois * (tauxVL / 100);
  const cfpMois = baseUrssafMois * (tauxCFP / 100);
  const cotisationsTotalesMois = urssafMois + vlMois + cfpMois;
  
  // TVA DU MOIS = TVA collectée du mois - TVA déductible du mois (pas le cumul global)
  const tvaAPayerMois = isTvaSubject ? Math.max(0, (mois.tva_collectee || 0) - (mois.tva_deductible || 0)) : 0;
  
  // Trésorerie du mois = CA Encaissé - Dépenses - Cotisations totales - TVA à payer du mois
  const tresorerieMois = (mois.ca_encaisse || 0) - (mois.depenses || 0) - cotisationsTotalesMois - tvaAPayerMois;
  
  // Calculs ANNÉE
  const baseUrssafAnnee = isTvaSubject ? (annee.ca_encaisse_ht || 0) : (annee.ca_encaisse || 0);
  const urssafAnnee = baseUrssafAnnee * (tauxURSSAF / 100);
  const vlAnnee = baseUrssafAnnee * (tauxVL / 100);
  const cfpAnnee = baseUrssafAnnee * (tauxCFP / 100);
  const cotisationsTotalesAnnee = urssafAnnee + vlAnnee + cfpAnnee;
  
  // TVA année = TVA collectée année - TVA déductible année
  const tvaAPayerAnnee = isTvaSubject ? Math.max(0, (annee.tva_collectee || 0) - (annee.tva_deductible || 0)) : 0;
  const chargesAnnee = cotisationsTotalesAnnee + tvaAPayerAnnee + (annee.total_depenses || 0);
  const gainNetAnnee = (annee.ca_encaisse || 0) - chargesAnnee;

  const monthNames = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];

  const getMonthOptions = () => {
    const options = [];
    const now = new Date();
    for (let i = 0; i < 12; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      const label = `${monthNames[d.getMonth()]} ${d.getFullYear()}`;
      options.push({ value, label });
    }
    return options;
  };

  const getMonthLabel = () => {
    const [year, month] = selectedMonth.split('-');
    return `${monthNames[parseInt(month) - 1]} ${year}`;
  };

  const completedCount = checklist.filter(item => item.done).length;

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-muted rounded w-48"></div>
          <div className="h-64 bg-muted rounded-xl"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <h1 className="text-2xl font-bold font-['Manrope']">Tableau de bord</h1>
        
        <div className="flex items-center gap-3 flex-wrap">
          {/* Toggle TVA */}
          <div className="flex items-center gap-2 bg-white border rounded-lg px-3 py-2">
            <Label htmlFor="tva-toggle" className="text-sm font-medium cursor-pointer">
              {isTvaSubject ? 'Assujetti TVA' : 'Franchise TVA'}
            </Label>
            <Switch 
              id="tva-toggle"
              checked={isTvaSubject}
              onCheckedChange={setIsTvaSubject}
            />
          </div>
          
          {/* Toggle Versement Libératoire */}
          <div className="flex items-center gap-2 bg-white border rounded-lg px-3 py-2">
            <Label htmlFor="vl-toggle" className="text-sm font-medium cursor-pointer">
              {versementLiberatoire ? `VL IR (${getTauxVersementLiberatoire()}%)` : 'Sans VL'}
            </Label>
            <Switch 
              id="vl-toggle"
              checked={versementLiberatoire}
              onCheckedChange={setVersementLiberatoire}
            />
          </div>
          
          <select 
            value={typeActivite}
            onChange={(e) => setTypeActivite(e.target.value)}
            className="text-sm border rounded-lg px-3 py-2 bg-white"
          >
            <option value="BIC_VENTE">BIC Vente (12.3%)</option>
            <option value="BIC_SERVICE">BIC Service (21.2%)</option>
            <option value="BNC">BNC (25.6%)</option>
            <option value="BNC_CIPAV">BNC CIPAV (23.2%)</option>
          </select>
          
          <Select value={selectedMonth} onValueChange={setSelectedMonth}>
            <SelectTrigger className="w-[180px]">
              <Calendar className="h-4 w-4 mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {getMonthOptions().map(opt => (
                <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* ========== WIDGET ANTICIPEZ RÉFORME 2026 ========== */}
      <Link to="/conformite-2026" className="block group" data-testid="widget-conformite-2026">
        <div className="bg-gradient-to-r from-blue-50 to-emerald-50 border border-blue-200 rounded-xl p-4 flex items-center justify-between gap-4 hover:shadow-md transition-all">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center flex-shrink-0">
              <Shield className="h-5 w-5 text-white" />
            </div>
            <div>
              <p className="font-semibold text-sm flex items-center gap-2 flex-wrap">
                <span>Anticipez la réforme facturation électronique 2026</span>
                <span className="text-[10px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-full font-bold">NOUVEAU</span>
              </p>
              <p className="text-xs text-muted-foreground mt-0.5">
                MON AE vous aide à anticiper les évolutions de la facturation électronique — découvrez comment.
              </p>
            </div>
          </div>
          <span className="text-blue-700 text-sm font-medium hidden sm:inline-flex items-center gap-1 group-hover:gap-2 transition-all">
            En savoir plus →
          </span>
        </div>
      </Link>

      {/* ========== VUE MOIS ========== */}
      <Card className="border-2 border-primary shadow-md">
        <CardHeader className="pb-3 bg-primary/5">
          <CardTitle className="text-xl font-['Manrope'] flex items-center gap-2">
            <Calendar className="h-5 w-5 text-primary" />
            {getMonthLabel()}
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-4 space-y-4">
          
          {/* FACTURES */}
          <div>
            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">Factures</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="bg-emerald-50 rounded-lg p-4 border border-emerald-200">
                <div className="flex items-center gap-2 mb-1">
                  <ArrowDown className="h-4 w-4 text-emerald-600" />
                  <span className="text-xs text-emerald-700 font-medium">Encaissé</span>
                </div>
                <p className="text-xl font-bold text-emerald-700">{formatCurrency(mois.ca_encaisse)}</p>
                <p className="text-xs text-emerald-600">{mois.factures_payees || 0} payée(s)</p>
              </div>

              <div className="bg-amber-50 rounded-lg p-4 border border-amber-200">
                <div className="flex items-center gap-2 mb-1">
                  <Clock className="h-4 w-4 text-amber-600" />
                  <span className="text-xs text-amber-700 font-medium">En attente</span>
                </div>
                <p className="text-xl font-bold text-amber-700">{formatCurrency(mois.montant_a_recevoir)}</p>
                <p className="text-xs text-amber-600">{mois.factures_a_recevoir || 0} facture(s)</p>
              </div>

              {/* Factures à relancer - utilise les données GLOBALES car les retards ne sont pas liés au mois */}
              <div className={`rounded-lg p-4 border ${(stats?.global?.factures_en_retard || 0) > 0 ? 'bg-red-50 border-red-300' : 'bg-gray-50 border-gray-200'}`}>
                <div className="flex items-center gap-2 mb-1">
                  <AlertTriangle className={`h-4 w-4 ${(stats?.global?.factures_en_retard || 0) > 0 ? 'text-red-600' : 'text-gray-400'}`} />
                  <span className={`text-xs font-medium ${(stats?.global?.factures_en_retard || 0) > 0 ? 'text-red-700' : 'text-gray-500'}`}>À relancer</span>
                </div>
                <p className={`text-xl font-bold ${(stats?.global?.factures_en_retard || 0) > 0 ? 'text-red-700' : 'text-gray-400'}`}>
                  {formatCurrency(stats?.global?.montant_en_retard || 0)}
                </p>
                <p className={`text-xs ${(stats?.global?.factures_en_retard || 0) > 0 ? 'text-red-600' : 'text-gray-400'}`}>
                  {stats?.global?.factures_en_retard || 0} en retard
                </p>
              </div>

              {/* Devis acceptés - utilise les données GLOBALES pour voir tous les devis à facturer */}
              <div className={`rounded-lg p-4 border ${(stats?.global?.devis_non_factures || 0) > 0 ? 'bg-indigo-50 border-indigo-200' : 'bg-gray-50 border-gray-200'}`}>
                <div className="flex items-center gap-2 mb-1">
                  <FileText className={`h-4 w-4 ${(stats?.global?.devis_non_factures || 0) > 0 ? 'text-indigo-600' : 'text-gray-400'}`} />
                  <span className={`text-xs font-medium ${(stats?.global?.devis_non_factures || 0) > 0 ? 'text-indigo-700' : 'text-gray-500'}`}>Devis acceptés</span>
                </div>
                <p className={`text-xl font-bold ${(stats?.global?.devis_non_factures || 0) > 0 ? 'text-indigo-700' : 'text-gray-400'}`}>
                  {formatCurrency(stats?.global?.montant_devis_non_factures || 0)}
                </p>
                <p className={`text-xs ${(stats?.global?.devis_non_factures || 0) > 0 ? 'text-indigo-600' : 'text-gray-400'}`}>
                  {stats?.global?.devis_non_factures || 0} à facturer
                </p>
              </div>
            </div>
          </div>

          {/* CHARGES */}
          <div>
            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">Charges & Cotisations</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="bg-rose-50 rounded-lg p-4 border border-rose-200">
                <div className="flex items-center gap-2 mb-1">
                  <ArrowUp className="h-4 w-4 text-rose-600" />
                  <span className="text-xs text-rose-700 font-medium">Dépenses</span>
                </div>
                <p className="text-xl font-bold text-rose-700">{formatCurrency(mois.depenses)}</p>
                <p className="text-xs text-rose-600">Fournisseurs</p>
              </div>

              {/* Cotisations URSSAF + VL + CFP */}
              <div className="bg-cyan-50 rounded-lg p-4 border border-cyan-200">
                <div className="flex items-center gap-2 mb-1">
                  <Calculator className="h-4 w-4 text-cyan-600" />
                  <span className="text-xs text-cyan-700 font-medium">Cotisations</span>
                </div>
                <p className="text-xl font-bold text-cyan-700">{formatCurrency(cotisationsTotalesMois)}</p>
                <p className="text-xs text-cyan-600">
                  {tauxTotal.toFixed(1)}% sur {isTvaSubject ? 'CA HT' : 'CA TTC'}
                </p>
                <div className="mt-2 pt-2 border-t border-cyan-200 text-xs text-cyan-600 space-y-0.5">
                  <div className="flex justify-between">
                    <span>URSSAF</span>
                    <span>{formatCurrency(urssafMois)}</span>
                  </div>
                  {versementLiberatoire && (
                    <div className="flex justify-between">
                      <span>IR (VL)</span>
                      <span>{formatCurrency(vlMois)}</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span>CFP</span>
                    <span>{formatCurrency(cfpMois)}</span>
                  </div>
                </div>
              </div>

              {/* TVA À PAYER - montant du mois */}
              <div className={`rounded-lg p-4 border ${isTvaSubject && tvaAPayerMois > 0 ? 'bg-violet-50 border-violet-300' : 'bg-gray-50 border-gray-200'}`}>
                <div className="flex items-center gap-2 mb-1">
                  <Receipt className={`h-4 w-4 ${isTvaSubject && tvaAPayerMois > 0 ? 'text-violet-600' : 'text-gray-400'}`} />
                  <span className={`text-xs font-medium ${isTvaSubject && tvaAPayerMois > 0 ? 'text-violet-700' : 'text-gray-500'}`}>TVA à payer</span>
                </div>
                <p className={`text-xl font-bold ${isTvaSubject && tvaAPayerMois > 0 ? 'text-violet-700' : 'text-gray-400'}`}>
                  {isTvaSubject ? formatCurrency(tvaAPayerMois) : '-'}
                </p>
                <p className={`text-xs ${isTvaSubject && tvaAPayerMois > 0 ? 'text-violet-600' : 'text-gray-400'}`}>
                  {isTvaSubject ? 'Voir onglet TVA' : 'Non assujetti'}
                </p>
              </div>

              <div className={`rounded-lg p-4 border ${(mois.depenses_en_attente || 0) > 0 ? 'bg-orange-50 border-orange-200' : 'bg-gray-50 border-gray-200'}`}>
                <div className="flex items-center gap-2 mb-1">
                  <Clock className={`h-4 w-4 ${(mois.depenses_en_attente || 0) > 0 ? 'text-orange-600' : 'text-gray-400'}`} />
                  <span className={`text-xs font-medium ${(mois.depenses_en_attente || 0) > 0 ? 'text-orange-700' : 'text-gray-500'}`}>Fournisseurs à payer</span>
                </div>
                <p className={`text-xl font-bold ${(mois.depenses_en_attente || 0) > 0 ? 'text-orange-700' : 'text-gray-400'}`}>
                  {formatCurrency(mois.montant_depenses_en_attente)}
                </p>
                <p className={`text-xs ${(mois.depenses_en_attente || 0) > 0 ? 'text-orange-600' : 'text-gray-400'}`}>
                  {mois.depenses_en_attente || 0} facture(s)
                </p>
              </div>
            </div>
          </div>

          {/* TRÉSORERIE */}
          <div className={`rounded-xl p-4 sm:p-5 ${tresorerieMois >= 0 ? 'bg-emerald-100 border-2 border-emerald-400' : 'bg-red-100 border-2 border-red-400'}`} data-testid="tresorerie-mois-card">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="flex items-center gap-3 min-w-0">
                <div className={`w-12 h-12 flex-shrink-0 rounded-full flex items-center justify-center ${tresorerieMois >= 0 ? 'bg-emerald-200' : 'bg-red-200'}`}>
                  <Euro className={`h-6 w-6 ${tresorerieMois >= 0 ? 'text-emerald-700' : 'text-red-700'}`} />
                </div>
                <div className="min-w-0">
                  <p className={`text-sm font-medium ${tresorerieMois >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>TRÉSORERIE DU MOIS</p>
                  <p className={`text-xs ${tresorerieMois >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                    Encaissé - Dépenses - Cotisations{isTvaSubject ? ' - TVA' : ''}
                  </p>
                </div>
              </div>
              <p className={`text-2xl sm:text-3xl font-bold whitespace-nowrap self-end sm:self-auto ${tresorerieMois >= 0 ? 'text-emerald-700' : 'text-red-700'}`} data-testid="tresorerie-mois-amount">
                {formatCurrency(tresorerieMois)}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ========== CUMUL ANNÉE (plus petit, gris) ========== */}
      <div className="bg-slate-100 rounded-xl p-4 border border-slate-200">
        <h3 className="text-sm font-semibold text-slate-600 uppercase tracking-wide mb-3 flex items-center gap-2">
          <TrendingUp className="h-4 w-4" />
          Cumul Année {stats?.current_year || new Date().getFullYear()}
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="text-center p-3 bg-white rounded-lg">
            <p className="text-xs text-slate-500 mb-1">CA Total</p>
            <p className="text-lg font-bold text-slate-700">{formatCurrency(annee.ca_encaisse)}</p>
          </div>
          <div className="text-center p-3 bg-white rounded-lg">
            <p className="text-xs text-slate-500 mb-1">Charges totales</p>
            <p className="text-lg font-bold text-slate-700">{formatCurrency(chargesAnnee)}</p>
          </div>
          <div className="text-center p-3 bg-white rounded-lg">
            <p className="text-xs text-slate-500 mb-1">Nb Factures</p>
            <p className="text-lg font-bold text-slate-700">{stats?.factures?.total || 0}</p>
          </div>
          <div className={`text-center p-3 rounded-lg ${gainNetAnnee >= 0 ? 'bg-emerald-50' : 'bg-red-50'}`}>
            <p className={`text-xs mb-1 ${gainNetAnnee >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>Gain net (hors IR)</p>
            <p className={`text-lg font-bold ${gainNetAnnee >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>{formatCurrency(gainNetAnnee)}</p>
          </div>
        </div>
      </div>

      {/* ========== OUTILS + CHECKLIST ========== */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <FileText className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-800">Créer factures & devis</h3>
                  <p className="text-xs text-muted-foreground">InvoiceHome gratuit</p>
                </div>
              </div>
              <a 
                href="https://invoicehome.com/signin?locale=fr" 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
              >
                Accéder <ExternalLink className="h-3 w-3" />
              </a>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2 pt-4">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base font-['Manrope']">Checklist du mois</CardTitle>
              <span className="text-xs text-muted-foreground">{completedCount}/{checklist.length}</span>
            </div>
            <Progress value={(completedCount / checklist.length) * 100} className="h-1.5" />
          </CardHeader>
          <CardContent className="pt-2">
            <div className="space-y-1">
              {checklist.map((item) => (
                <button 
                  key={item.id}
                  onClick={() => toggleChecklistItem(item.id)}
                  className={`w-full flex items-center gap-2 p-2 rounded-lg transition-all cursor-pointer text-left text-sm ${
                    item.done ? 'bg-emerald-50' : 'hover:bg-muted/50'
                  }`}
                >
                  <div className={`w-4 h-4 rounded border flex items-center justify-center ${
                    item.done ? 'bg-emerald-500 border-emerald-500' : 'border-gray-300'
                  }`}>
                    {item.done && <CheckCircle className="w-3 h-3 text-white" />}
                  </div>
                  <span className={item.done ? 'text-emerald-700 line-through' : ''}>{item.label}</span>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* CTA pour non-payants */}
      {!user?.has_paid && (
        <Card className="bg-gradient-to-r from-[#10B981] to-[#059669] border-0 shadow-lg">
          <CardContent className="p-6 text-center">
            <h3 className="text-xl font-bold text-white mb-2">Envie d'accéder à tout ça ?</h3>
            <p className="text-white/90 mb-4">Dashboard complet + 9 ressources Patrithèque exclusives</p>
            <a href="/demo?step=4" className="inline-block bg-white text-[#10B981] font-bold py-3 px-6 rounded-xl hover:bg-gray-100 transition-colors">
              Page de vente
            </a>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default DashboardPage;
